import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { EmployeeDto } from '../../../../core/data/new-dtos/employee.dtos';
import { SupervisorBranchDto } from '../../../../core/data/new-dtos/supervisor.dtos';
import { EmployeesService } from '../../services/employees.service';

export interface EmployeeModalResult {
  action: 'save' | 'cancel';
  data?: {
    name: string;
    lastName: string;
    branchId: number;
    managerId: number | null;
  };
}

@Component({
  selector: 'app-employee-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './employee-modal.component.html',
})
export class EmployeeModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() employee!: EmployeeDto;
  @Input() branches: SupervisorBranchDto[] = [];
  @Input() isVisible = false;
  @Output() closed = new EventEmitter<EmployeeModalResult>();

  form!: FormGroup;
  managers: EmployeeDto[] = [];
  loadingManagers = false;

  constructor(
    private fb: FormBuilder,
    private employeesService: EmployeesService,
    private elementRef: ElementRef,
  ) { }

  get isManagerRole(): boolean {
    return (this.employee?.role || '').toLowerCase() === 'manager';
  }

  async ngOnInit() {
    this.form = this.fb.group({
      name: [this.employee?.name ?? '', Validators.required],
      lastName: [this.employee?.lastName ?? '', Validators.required],
      branchId: [this.employee?.branchId ?? null, Validators.required],
      managerId: [this.employee?.managerId ?? null],
    });

    if (!this.isManagerRole) {
      await this.loadManagers(this.employee?.branchId ?? null);

      this.form.get('branchId')!.valueChanges.subscribe(async (branchId) => {
        // A manager from the previous branch is no longer a valid choice
        this.form.get('managerId')!.setValue(null);
        await this.loadManagers(branchId ? Number(branchId) : null);
      });
    }
  }

  // Bootstrap modals must sit at the top level to escape the surrounding stacking context
  ngAfterViewInit(): void {
    document.body.appendChild(this.elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    const element = this.elementRef.nativeElement;
    if (element?.parentNode === document.body) {
      document.body.removeChild(element);
    }
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal')) {
      this.onCancel();
    }
  }

  onCancel(): void {
    this.closed.emit({ action: 'cancel' });
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value;
    this.closed.emit({
      action: 'save',
      data: {
        name: value.name.trim(),
        lastName: value.lastName.trim(),
        branchId: Number(value.branchId),
        managerId: value.managerId ? Number(value.managerId) : null,
      },
    });
  }

  private async loadManagers(branchId: number | null) {
    if (!branchId) {
      this.managers = [];
      return;
    }

    this.loadingManagers = true;
    try {
      // The API scopes this list to what the caller is allowed to see
      this.managers = await lastValueFrom(
        this.employeesService.getScoped({
          branchId,
          role: 'Manager',
          status: 'Active',
        }),
      );
    } catch {
      this.managers = [];
    } finally {
      this.loadingManagers = false;
    }
  }
}
